import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Minus,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo2,
  Redo2,
  Eraser,
} from "lucide-react";
import { useCallback, useEffect } from "react";

function ToolButton({ active, disabled, onClick, title, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 transition-colors border border-transparent ${
        active
          ? "bg-brand-700 text-white"
          : "text-ink-soft hover:text-brand-700 hover:bg-brand-100"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // We want only H2 and H3 (no H1 inside body — H1 is the post title)
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({
        HTMLAttributes: { class: "block" },
        inline: false,
      }),
      Placeholder.configure({
        placeholder: placeholder || "Write the post...",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "prose-blog min-h-[420px] focus:outline-none px-6 py-6 bg-white",
      },
    },
  });

  // Keep editor in sync if parent value changes externally (e.g., loading a post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") && (value || "") !== current) {
      editor.commands.setContent(value || "", false);
    } else if (!value && current && current !== "<p></p>") {
      editor.commands.setContent("", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const promptLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link")?.href || "";
    const url = window.prompt("Link URL (leave empty to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    let safe = url.trim();
    if (!/^https?:\/\//i.test(safe) && !safe.startsWith("mailto:") && !safe.startsWith("/")) {
      safe = `https://${safe}`;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
  }, [editor]);

  const promptImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL:");
    if (!url) return;
    editor.chain().focus().setImage({ src: url.trim(), alt: "" }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-ink-line bg-white" data-testid="rich-text-editor">
      <div
        className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-ink-line bg-brand-50/40"
        data-testid="editor-toolbar"
      >
        <ToolButton
          title="Heading 2"
          testId="editor-h2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Heading 3"
          testId="editor-h3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-6 bg-ink-line mx-1" />
        <ToolButton
          title="Bold"
          testId="editor-bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Italic"
          testId="editor-italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-6 bg-ink-line mx-1" />
        <ToolButton
          title="Bullet list"
          testId="editor-bullet"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Numbered list"
          testId="editor-ordered"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Blockquote"
          testId="editor-quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-6 bg-ink-line mx-1" />
        <ToolButton
          title="Insert link"
          testId="editor-link"
          active={editor.isActive("link")}
          onClick={promptLink}
        >
          <LinkIcon className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Insert image by URL"
          testId="editor-image"
          onClick={promptImage}
        >
          <ImageIcon className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Horizontal rule"
          testId="editor-hr"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus className="w-4 h-4" />
        </ToolButton>
        <span className="w-px h-6 bg-ink-line mx-1" />
        <ToolButton
          title="Clear formatting"
          testId="editor-clear"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <Eraser className="w-4 h-4" />
        </ToolButton>
        <span className="flex-1" />
        <ToolButton
          title="Undo"
          testId="editor-undo"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton
          title="Redo"
          testId="editor-redo"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="w-4 h-4" />
        </ToolButton>
      </div>
      <EditorContent editor={editor} data-testid="editor-content" />
    </div>
  );
}
